import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FeatureModuleEnum } from '@libs/backend-common';
import { useQuery } from '@apollo/client';
import { useTheme } from 'styled-components';

import { FrStatuses, meQuery, UserResponse } from '@libs/graphql';

import {
  ConvertDetails,
  FeatureRenderer,
  TradePageCalculator,
  TransactionLayout,
  VerifyUserModal,
} from 'src/components';
import { CurrencyType, TradeType } from 'src/constants';
import {
  useApiResponse,
  useModal,
  useTransactionStepper,
  useTranslation,
} from 'src/hooks';
import {
  ExchangeCurrencyOption,
  DEFAULT_AMOUNT,
  TradeFormNew,
  AssetAmountNewType,
} from 'src/types';

import { Body, Page } from './styles';

const TradePageGe = () => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();
  const { type } = useParams();
  const [changedInputName, setChangedInputName] =
    useState<AssetAmountNewType>();
  const defaultFromAssetCode = searchParams.get('fromAsset');
  const defaultToAssetCode = searchParams.get('toAsset');
  const { t } = useTranslation();
  const { currentStep, prevStep, nextStep } = useTransactionStepper();
  const [form, setForm] = useState<TradeFormNew>({
    fromAssetAmount: DEFAULT_AMOUNT,
    toAssetAmount: DEFAULT_AMOUNT,
    type: undefined,
  });

  const [tradeType, setTradeType] = useState<TradeType>(TradeType.BUY);
  const [fromAsset, setFromAsset] = useState<ExchangeCurrencyOption>();
  const [toAsset, setToAsset] = useState<ExchangeCurrencyOption>();

  const title =
    currentStep === 0
      ? t('wallet_action_convert')
      : t('trade_look_at_the_details');

  const handleFirstButtonChange = () => {
    prevStep();
    setChangedInputName(AssetAmountNewType.FROMASSET);
  };
  const handleSecondButtonChange = () => {
    prevStep();
    setChangedInputName(AssetAmountNewType.TOASSET);
  };

  const {
    Dialog: UnverifiedDialog,
    openModal: openUnverifiedModal,
    closeModal: closeUnverifiedModal,
  } = useModal();

  const handleMeSueccess = (data: UserResponse) => {
    if (data.me.frStatus !== FrStatuses.VERIFIED) {
      openUnverifiedModal();
    }
  };

  const meRes = useQuery(meQuery);

  const { response: meResponse } = useApiResponse(meRes, handleMeSueccess);

  const handleTradePageContinue = () => {
    if (meResponse?.me.frStatus !== FrStatuses.VERIFIED) {
      openUnverifiedModal();
    } else {
      nextStep();
    }
  };

  return (
    <FeatureRenderer
      acceptedFeature={[FeatureModuleEnum.EXCHANGE]}
      shouldNavigate={true}
      shouldCheckKyc={false}
    >
      <Page>
        <TransactionLayout
          onGoBack={prevStep}
          headTitle={title}
          isBackButtonVisible={currentStep > 0}
        >
          <Body>
            {currentStep === 0 ? (
              <TradePageCalculator
                nextStep={handleTradePageContinue}
                defaultFromAssetCode={
                  defaultFromAssetCode ? defaultFromAssetCode : undefined
                }
                defaultToAssetCode={
                  defaultToAssetCode ? defaultToAssetCode : undefined
                }
                tradeType={tradeType}
                setTradeType={setTradeType}
                fromAsset={fromAsset}
                setFromAsset={setFromAsset}
                toAsset={toAsset}
                setToAsset={setToAsset}
                form={form}
                setForm={setForm}
                type={type}
                changedInputName={changedInputName}
                isSwappable
              />
            ) : (
              <ConvertDetails
                isConvert
                form={form}
                fromAssetCode={fromAsset?.value as CurrencyType}
                toAssetCode={toAsset?.value as CurrencyType}
                convertType={tradeType}
                secondButtonHandler={handleSecondButtonChange}
                firstButtonHandler={handleFirstButtonChange}
              />
            )}
          </Body>
        </TransactionLayout>
      </Page>
      <UnverifiedDialog
        title=""
        shouldHideHeaderBorder
        headerBackground={colors.surface.default}
      >
        <VerifyUserModal
          title={t('trade_crypto_kyc_warning')}
          closeModal={closeUnverifiedModal}
        />
      </UnverifiedDialog>
    </FeatureRenderer>
  );
};

export default TradePageGe;
